import React from 'react';
import { Link } from 'react-router-dom';
import { PLAN_GRID_VIEWS } from "components/Plans";
import i18next from "i18next";
import { getMultigroupSectionVars } from "utils/multigroupPlanGrouping";

const formatCounterValue = (value) =>
	new Intl.NumberFormat("de-DE").format(parseFloat(value).toFixed(2));

const getCounterHeader = (props) => props.account_assignments[0].counters.map((d, i) => {
	const headerContent = d.account?.name || d.transaction_rule?.name || '';
	return (
		<th className="th-counter" key={i} title={headerContent}>
			<div className="th-counter-content">
				{headerContent}
			</div>
		</th>
	)
});

const getCounterRows = (props) => props.account_assignments.map((assignment, i) => {
	const counters = assignment.counters;
	const employee = assignment.employee;

	return (
		<tr key={employee?.id ?? i}>
			{getAccountRowContent(props, employee, counters, i)}
		</tr>
	)
});

const getCounterTotalsRow = (props) => {
	const assignments = props.account_assignments;
	const columns = assignments[0].counters;

	return (
		<tr className="counter-totals-row">
			{columns.map((column, colIndex) => {
				let sum = 0;
				assignments.forEach((assignment) => {
					const item = assignment.counters[colIndex];
					const value = parseFloat(item && item.value);
					if (!isNaN(value)) sum += value;
				});

				const isEuros = column.account && column.account.type === "Euros";
				const formattedValue = formatCounterValue(sum);

				return (
					<td
						key={colIndex}
						style={{ textAlign: "center", fontWeight: "bold" }}
					>
						{formattedValue}
						{isEuros && "€"}
					</td>
				);
			})}
		</tr>
	);
};

const getTotalsCells = (columns, totals) =>
	columns.map((column, colIndex) => {
		const sum = totals[colIndex] || 0;
		const isEuros = column.account && column.account.type === "Euros";
		return (
			<td
				key={colIndex}
				style={{ textAlign: "center", fontWeight: "bold" }}
			>
				{formatCounterValue(sum)}
				{isEuros && "€"}
			</td>
		);
	});

const getAccountRowContent = (props, employee, counters, row) => counters.map((item, i) => {
	var value = parseFloat(item.value).toFixed(2);
	const { start, end } = props;
	const formattedValue = new Intl.NumberFormat("de-DE").format(value);

	if (item.account && value > 0) {
		return (
			<td key={i} style={{ textAlign: "center" }}>
				<Link to={`/transactions/${employee.id}/${item.account.id}/${start}@${end}`} target="_blank" className="counter-value" title={i18next.t('plan.viewTransaction')}>{formattedValue} {item.account.type === "Euros" && "€"}</Link>
			</td>
		)
	}
	return (
		<td key={i} style={{ textAlign: "center" }}>
			{formattedValue}{item.account && item.account.type === "Euros" && "€"}
		</td>
	)
});

const emptyCounterCells = (count) =>
	Array.from({ length: count }, (_, i) => (
		<td key={i} style={{ textAlign: "center" }} />
	));

const getGroupedCounterBodies = (props) => {
	const columns = props.account_assignments[0].counters;
	const columnCount = columns.length;

	return (props.multigroupSections || []).map((section, sectionIndex) => {
		const groupId = section.group.id;
		const sectionVars = getMultigroupSectionVars(section.group, sectionIndex);
		const sectionClass = section.collapsed
			? "multigroup-section is-collapsed"
			: "multigroup-section";

		return (
			<tbody
				key={`mg-section-${groupId}`}
				className={sectionClass}
				style={sectionVars}
			>
				<tr className="multigroup-section-header">
					<td colSpan={columnCount} className="multigroup-section-fill" />
				</tr>
				{section.rows.map((row) => {
					const assignment = row.accountAssignment;
					if (!assignment) {
						return (
							<tr
								key={`mg-${groupId}-${row.sourceRowIndex}`}
							>
								{emptyCounterCells(columnCount)}
							</tr>
						);
					}
					return (
						<tr
							key={`mg-${groupId}-${assignment.employee?.id ?? row.sourceRowIndex}`}
						>
							{getAccountRowContent(
								props,
								assignment.employee,
								assignment.counters,
								row.sourceRowIndex
							)}
						</tr>
					);
				})}
				<tr className="multigroup-section-subtotal">
					{getTotalsCells(columns, section.subtotals || [])}
				</tr>
			</tbody>
		);
	});
};

const getUniqueTotalsRow = (props) => {
	const columns = props.account_assignments[0].counters;
	return (
		<tr className="counter-totals-row">
			{getTotalsCells(columns, props.uniqueTotals || [])}
		</tr>
	);
};

const PlanCounters = (props) => {
	var tableClass = "";
	if (props.plan_grid_view === PLAN_GRID_VIEWS.ubications)
		tableClass = "table-ubications"

	if (props.account_assignments.length === 0) return "";

	const isGrouped =
		props.multigroupSections?.length > 0 &&
		props.plan_grid_view === PLAN_GRID_VIEWS.default;

	const columnCount = props.account_assignments[0].counters.length;

	return (
		<div style={{ minWidth: 0, width: "100%", overflowX: "auto" }}>
			<table id="tableCounters" className={`table table-bordered table-counter ${tableClass}`}>
				<thead>
					{props.headerToolbar && (
						<tr className="counter-toolbar-row">
							<th colSpan={columnCount} className="th-counter-toolbar">
								{props.headerToolbar}
							</th>
						</tr>
					)}
					<tr>
						{getCounterHeader(props)}
					</tr>
				</thead>
				{isGrouped ? (
					getGroupedCounterBodies(props)
				) : (
					<tbody>{getCounterRows(props)}</tbody>
				)}
				<tfoot>
					{isGrouped ? getUniqueTotalsRow(props) : getCounterTotalsRow(props)}
				</tfoot>
			</table>
		</div>
	)

}

export default React.memo(PlanCounters, (prevProps, nextProps) => {
	return (
		prevProps.start === nextProps.start &&
		prevProps.end === nextProps.end &&
		prevProps.plan_grid_view === nextProps.plan_grid_view &&
		prevProps.account_assignments === nextProps.account_assignments &&
		prevProps.multigroupSections === nextProps.multigroupSections &&
		prevProps.uniqueTotals === nextProps.uniqueTotals &&
		prevProps.headerToolbar === nextProps.headerToolbar
	);
});
